import React, { useEffect, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { api } from '../services/api';
import { Chart, Filler, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend } from 'chart.js';
import Icon from '@mdi/react';
import { mdiAlert, mdiInvoicePlusOutline, mdiPlus } from '@mdi/js';
import '../styles/Dashboard.css';
import CreateTransaction from './CreateTransaction';
import useIsMobile from '../hooks/useIsMobile';

var style = getComputedStyle(document.body);

Chart.register(
  CategoryScale, // Register the 'category' scale
  LinearScale,   // Register the 'linear' scale (used for numerical data)
  BarElement,    // Register the bar element
  LineElement,   // Register the line element
  PointElement,  // Register the point element (used in line charts)
  Tooltip,       // Register the tooltip plugin
  Legend,         // Register the legend plugin
  Filler
);

const Dashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [balanceData, setBalanceData] = useState({});
  const [incomeExpenseData, setIncomeExpenseData] = useState({});
  const [conversionRates, setConversionRates] = useState({}); // Store conversion rates
  const [expandedGroups, setExpandedGroups] = useState({}); // Track expanded/collapsed state
  const [loading, setLoading] = useState(true);
  const [upcomingTransactions, setUpcomingTransactions] = useState([]);
  const [showCreateRecurrencePanel, setShowCreateRecurrencePanel] = useState(false);
  const [showCreateTransactionPanel, setShowCreateTransactionPanel] = useState(false);
  const [selectedRecurrence, setSelectedRecurrence] = useState(null);
  const [invitationBanner, setInvitationBanner] = useState(() => localStorage.getItem('invitationAccepted') === 'true');

  const dismissInvitationBanner = () => {
    setInvitationBanner(false);
    localStorage.removeItem('invitationAccepted');
  };

  const today = new Date();
  const todayString = today.toISOString().substring(0, 10);
  const isMobile = useIsMobile();

  const dateDiffInDays = (date1, date2) => {
    const diffInMs = new Date(date1).getTime() - new Date(date2).getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 3600 * 24));
    return diffInDays;
  };

  function isTransactionExpired(transaction) {
    // Red icon: backend flagged it overdue, or next_run_date is before today.
    // Compare date-only (midnight-normalized) so time-of-day never skews the check.
    const nextRun = new Date(transaction.next_run_date);
    nextRun.setHours(0, 0, 0, 0);
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    return transaction.is_overdue || nextRun < todayStart;
  }
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data
        const [accountsResponse, balanceResponse, incomeExpenseResponse, upcomingRecurrencesResponse] = await Promise.all([
          api.get('/accounts-balance'),
          api.get('/balance-over-time'),
          api.get('/income-expense'),
          api.get('/recurrences/upcoming?days=15')
        ]);

        const filteredAccounts = accountsResponse.data.filter(x => x.is_open);
        setAccounts(filteredAccounts);
        setBalanceData(balanceResponse.data);
        setIncomeExpenseData(incomeExpenseResponse.data);
        setUpcomingTransactions(upcomingRecurrencesResponse.data.sort((a, b) => new Date(a.next_run_date) - new Date(b.next_run_date)));
        fetchConversionRates();

        // Fetch conversion rates
        const ratesResponse = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        const ratesData = await ratesResponse.json();
        setConversionRates(ratesData.rates);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // The invitation-accepted banner clears once the admin confirms the
  // membership, which makes /api/family/me return a role. Check on mount.
  useEffect(() => {
    if (!invitationBanner) return;
    api.get('/family/me')
      .then((res) => {
        if (res.data.role) {
          setInvitationBanner(false);
          localStorage.removeItem('invitationAccepted');
        }
      })
      .catch(() => { /* keep banner if the check fails */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const balanceChartData = {
    labels: balanceData.labels || [],
    datasets: balanceData.datasets || [],
  };

  const balanceChartOptions = () => {
    const padding = isMobile ? 5 : 10;
    const displayLegend = isMobile ? false : true;
    const displayAxisTitle = isMobile ? false : true;
    const fontSize = isMobile ? 10 : 12;
    const textColor = style.getPropertyValue('--color-text');

    let options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
      },
      layout: {
        padding: padding,
      },
      scales: {
        x: {
          title: {
            display: displayAxisTitle,
            text: 'Month',
            color: textColor,
          },
          grid: {
            display: true,
          },
          ticks: {
            autoSkip: true,
            padding: padding,
            color: textColor,
            major: {
              enabled: true,
            },
            font: {
              size: fontSize
            }
          },
          border: {
            color: textColor,
          }
        },
        y: {
          title: {
            display: displayAxisTitle,
            text: 'Balance',
            color: textColor,
          },
          grid: {
            display: true,
          },
          ticks: {
            autoSkip: true,
            padding: padding,
            color: textColor,
            major: {
              enabled: true,
            },
            font: {
              size: fontSize
            }
          },
          border: {
            color: textColor,
          }
        },
      },
      plugins: {
        tooltip: {
          mode: 'index',
        },
        legend: {
          display: displayLegend,
          position: 'top',
          labels: {
            color: textColor,
            padding: padding,
          },
        },
      },
    };

    return options;
  };

  // Chart data for income vs expense
  const incomeExpenseChartData = {
    labels: incomeExpenseData.labels || [],
    datasets: [
      {
        label: 'Income',
        data: incomeExpenseData.income || [],
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Expense',
        data: incomeExpenseData.expense || [],
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      },
      {
        label: 'Difference',
        data: incomeExpenseData.difference || [],
        borderColor: 'rgba(54, 162, 235, 1)', // Blue color for difference
        borderWidth: 2,
        fill: false,
        type: 'line',  // Use a line for the difference
      },
      {
        label: 'Cumulative Total',
        data: incomeExpenseData.cumulative_total || [],
        borderColor: 'rgba(153, 102, 255, 1)', // Purple color for cumulative total
        borderWidth: 2,
        fill: false,
        type: 'line',  // Use a line for the cumulative total
      },
    ],
  };

  const incomeExpenseChartOptions = () => {
    const padding = isMobile ? 5 : 10;
    const displayLegend = isMobile ? false : true;
    const displayAxisTitle = isMobile ? false : true;
    const fontSize = isMobile ? 10 : 12;
    const textColor = style.getPropertyValue('--color-text');

    let options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: padding,
      },
      scales: {
        x: {
          type: 'category',
          title: {
            display: displayAxisTitle,
            text: 'Month',
            color: textColor,
          },
          grid: {
            display: true,
          },
          ticks: {
            autoSkip: true,
            padding: padding,
            color: textColor,
            major: {
              enabled: true,
            },
            font: {
              size: fontSize
            }
          },
          border: {
            color: textColor,
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: displayAxisTitle,
            text: 'Amount',
            color: textColor,
          },
          grid: {
            display: true,
          },
          ticks: {
            autoSkip: true,
            padding: padding,
            color: textColor,
            major: {
              enabled: true,
            },
            font: {
              size: fontSize
            }
          },
          border: {
            color: textColor,
          }
        },
      },
      plugins: {
        legend: {
          display: displayLegend,
          position: 'top',
          labels: {
            color: textColor,
            padding: padding,
          },
        },
      },
    };

    return options;
  };

  const convertToEuros = (amount, currency) => {
    if (currency === 'EUR') return amount; // No conversion needed
    const rate = conversionRates[currency]; // Get conversion rate for the currency
    return rate ? amount / rate : amount; // Convert to Euros if rate is available
  };

  const fetchConversionRates = async () => {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR'); // Replace with your API key if needed
      const data = await response.json();
      setConversionRates(data.rates);
    } catch (error) {
      console.error('Error fetching conversion rates:', error);
    }
  };

  const currencySymbol = (code) => {
    switch (code) {
      case "EUR":
        return "€"
      case "USD":
        return "$"
      case "GBP":
        return "£"
      default:
        return ""
    }
  }

  // Group accounts by type and calculate totals
  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.type;
    if (!groups[type]) {
      groups[type] = {
        accounts: [],
        totalReconciled: 0,
        totalBalance: 0,
        totalReconciledEUR: 0,
        totalBalanceEUR: 0,
      };
    }
    groups[type].accounts.push(account);
    groups[type].totalReconciled += account.reconciled_balance;
    groups[type].totalBalance += account.balance;
    groups[type].totalReconciledEUR += convertToEuros(account.reconciled_balance, account.currency);
    groups[type].totalBalanceEUR += convertToEuros(account.balance, account.currency);

    return groups;
  }, {});

  for (const type in groupedAccounts) {
    groupedAccounts[type].accounts.sort((a, b) => a.name.localeCompare(b.name));
  }

  const toggleGroup = (type) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  return (
    <div className="card dashboard-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {invitationBanner && (
        <div className="invitation-banner">
          <div className="invitation-banner__content">
            <Icon path={mdiAlert} size={1} className="invitation-banner__icon" />
            <span>
              Invitation accepted. The family administrator has been informed and will complete your member configuration shortly.
            </span>
          </div>
          <button type="button" className="invitation-banner__close" onClick={dismissInvitationBanner} aria-label="Dismiss">&times;</button>
        </div>
      )}

      <div className="dashboard-header">
        <h2 className="page-title">Home</h2>
        <div className='buttons-bar'>
          <button
            className="btn btn--primary"
            data-tour="new-transaction"
            onClick={() => setShowCreateTransactionPanel(true)}
          >
            <Icon path={mdiPlus} size={0.8} />
            New Transaction
          </button>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-wrapper">
          <h2>Balance Over Time</h2>
          <Line
            data={balanceChartData}
            options={balanceChartOptions()}
          />
        </div>

        <div className="chart-wrapper">
          <h2>Monthly Income vs Expense</h2>
          <Bar
            data={incomeExpenseChartData}
            options={incomeExpenseChartOptions()}
          />
        </div>
      </div>

      <div className="tables-container">
        <div className="balances-table-container">
          <h2>Account Balances</h2>
          {isMobile ? (
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedAccounts).map(([type, group]) => (
                  <React.Fragment key={type}>
                    {/* Group Header with Totals */}
                    <tr className='account-group-header'
                      onClick={() => toggleGroup(type)}
                    >
                      <td>
                        {type} {expandedGroups[type] ? '▼' : '▶'}
                      </td>
                      <td>{group.totalBalanceEUR.toFixed(2)}</td>
                    </tr>
                    {expandedGroups[type] &&
                      group.accounts.map((account) => (
                        <tr key={account.id}>
                          <td>{account.name}</td>
                          <td>
                            {currencySymbol(account.currency)} {account.balance.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Currency</th>
                  <th>Reconciled</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedAccounts).map(([type, group]) => (
                  <React.Fragment key={type}>
                    {/* Group Header with Totals */}
                    <tr className='account-group-header'
                      onClick={() => toggleGroup(type)}
                    >
                      <td>
                        {type} {expandedGroups[type] ? '▼' : '▶'}
                      </td>
                      <td></td>
                      <td>€ {group.totalReconciledEUR.toFixed(2)}</td>
                      <td>€ {group.totalBalanceEUR.toFixed(2)}</td>
                    </tr>
                    {expandedGroups[type] &&
                      group.accounts.map((account) => (
                        <tr key={account.id}>
                          <td>{account.name}</td>
                          <td>{account.currency}</td>
                          <td>
                            {account.reconciled_balance.toFixed(2)}
                          </td>
                          <td>
                            {account.balance.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="upcoming-transactions-container">
          <h2>Upcoming Transactions (Next 15 Days)</h2>
          {upcomingTransactions.length === 0 ? (
            <p className="no-transactions">No upcoming transactions</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  {!isMobile && <th>Account</th>}
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcomingTransactions.map(transaction => (
                  <tr key={transaction.id} className={isTransactionExpired(transaction) ? 'overdue-transaction' : ''}>
                    <td>
                      {(isTransactionExpired(transaction)) && (
                        <Icon path={mdiAlert} size={1} className="warning-icon-red" title="This recurring transaction is overdue" />
                      )}
                      {!isTransactionExpired(transaction) && dateDiffInDays(transaction.next_run_date, today) <= 3 && (
                        <Icon path={mdiAlert} size={1} className="warning-icon-yellow" title="This recurring transaction is about to expire" />
                      )}
                      {new Date(transaction.next_run_date).toLocaleDateString()}
                    </td>
                    <td>{transaction.notes || 'No description'}</td>
                    {!isMobile && <td>{transaction.account_name}</td>}
                    <td className={transaction.amount < 0 ? 'negative' : 'positive'}>
                      {transaction.amount ? currencySymbol(transaction.currency) : ''}
                      {transaction.amount?.toFixed(2)}
                    </td>
                    <td>
                      <button
                        className="btn btn--ghost"
                        onClick={() => {
                          setSelectedRecurrence(transaction);
                          setShowCreateRecurrencePanel(true);
                        }}
                        title="Create next occurrence"
                      >
                        <Icon path={mdiInvoicePlusOutline} size={0.8} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateTransactionPanel && (
        <div className="edit-panel create-panel">
          <div className="panel-header">
            <h3>New Transaction</h3>
            <button onClick={() => setShowCreateTransactionPanel(false)}>×</button>
          </div>
          <CreateTransaction
            onCancel={() => setShowCreateTransactionPanel(false)}
            onSuccess={(id) => {
              setShowCreateTransactionPanel(false);
            }}
          />
        </div>
      )}

      {showCreateRecurrencePanel && (
        <div className="edit-panel create-panel">
          <div className="panel-header">
            <h3>Create Next Occurrence</h3>
            <button onClick={() => {
              setShowCreateRecurrencePanel(false);
              setSelectedRecurrence(null);
            }}>×</button>
          </div>
          <CreateTransaction
            existingTransaction={{
              ...selectedRecurrence,
              date: selectedRecurrence.next_run_date,
              recurrence_id: selectedRecurrence.id,
            }}
            onCancel={() => {
              setShowCreateRecurrencePanel(false);
              setSelectedRecurrence(null);
            }}
            onSuccess={(id) => {
              setShowCreateRecurrencePanel(false);
              setSelectedRecurrence(null);
              // Refresh the upcoming transactions after creating a new occurrence
              api.get('/recurrences/upcoming?days=15')
                .then(res => setUpcomingTransactions(res.data.sort((a, b) => new Date(a.next_run_date) - new Date(b.next_run_date))));
              // The new occurrence changes account totals — refresh them
              api.get('/accounts-balance')
                .then(res => setAccounts(res.data.filter(x => x.is_open)))
                .catch(err => console.error('Error refreshing account balances:', err));
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
